import { useState, useMemo } from 'react';
import PageHero from '../components/PageHero.jsx';
import FilterBar from '../components/FilterBar.jsx';
import RuleCard from '../components/RuleCard.jsx';
import InfoIcon from '../components/InfoIcon.jsx';
import { rules } from '../data/rules.js';
import { SOURCES } from '../data/sources.js';
import './Rules.css';

// ── Filter config ─────────────────────────────────────────────────────────────

const TOTAL = rules.length;

const CATEGORY_OPTIONS = ['All', 'Devices', 'Attendance', 'Integrity', 'Dress', 'Conduct', 'Safety'];
const SEVERITY_OPTIONS = ['All', 'High', 'Medium', 'Low'];

const FILTER_DEFAULTS = { category: 'All', severity: 'All' };

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Rules() {
  const [category, setCategory] = useState(FILTER_DEFAULTS.category);
  const [severity, setSeverity] = useState(FILTER_DEFAULTS.severity);
  const [search,   setSearch]   = useState('');

  const hasActiveFilters =
    category !== FILTER_DEFAULTS.category ||
    severity !== FILTER_DEFAULTS.severity ||
    search.trim() !== '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter(r => {
      if (category !== 'All' && r.category !== category.toLowerCase()) return false;
      if (severity !== 'All' && r.severity !== severity.toLowerCase()) return false;
      if (q) {
        const inTitle  = r.title.toLowerCase().includes(q);
        const inShort  = r.shortTitle && r.shortTitle.toLowerCase().includes(q);
        const inDesc   = r.description.toLowerCase().includes(q);
        if (!inTitle && !inShort && !inDesc) return false;
      }
      return true;
    });
  }, [category, severity, search]);

  function handleFilterChange(key, value) {
    if (key === 'category') setCategory(value);
    if (key === 'severity') setSeverity(value);
  }

  function handleClear() {
    setCategory(FILTER_DEFAULTS.category);
    setSeverity(FILTER_DEFAULTS.severity);
    setSearch('');
  }

  const filters = [
    { key: 'category', label: 'Category', options: CATEGORY_OPTIONS, value: category },
    { key: 'severity', label: 'Severity', options: SEVERITY_OPTIONS, value: severity },
  ];

  return (
    <div className="rules-page">
      <PageHero
        icon="/assets/eagle/page-icons/rules.png"
        title="School rules"
      />
      <p className="rules-subtitle">
        The big ones, with consequences and exceptions. Tap any rule to expand.
      </p>

      {/* ── HB 1481 callout — always visible ── */}
      <div className="rules-hb1481-callout">
        <div className="rules-hb1481-header">
          <span className="rules-hb1481-icon" aria-hidden="true">📵</span>
          <h2 className="rules-hb1481-title">
            No phones during school (HB 1481)
          </h2>
          <InfoIcon source={SOURCES.hb1481} />
        </div>
        <p className="rules-hb1481-body">
          Phones, smartwatches, earbuds, and tablets stay off and out of sight from first bell to last bell — including lunch and passing periods. This is a Texas state law.
        </p>
        <p className="rules-hb1481-body">
          Confiscated devices are returned to a parent. Repeat violations have additional consequences.
        </p>
        <p className="rules-hb1481-body rules-hb1481-exceptions">
          Exceptions exist for IEP/504 accommodations and documented medical needs.
        </p>
      </div>

      {/* ── Filters ── */}
      <FilterBar
        filters={filters}
        search={{ value: search, placeholder: 'Search rules…' }}
        onChange={handleFilterChange}
        onSearchChange={setSearch}
        onClear={handleClear}
        hasActiveFilters={hasActiveFilters}
      />

      {/* ── Result count ── */}
      <p className="rules-count">
        Showing <strong>{filtered.length}</strong> of {TOTAL} rules
      </p>

      {/* ── Rule grid ── */}
      {filtered.length > 0 ? (
        <div className="rules-grid">
          {filtered.map(rule => (
            <RuleCard key={rule.id} rule={rule} />
          ))}
        </div>
      ) : (
        <div className="rules-empty">
          <p>No rules match those filters.</p>
          <button type="button" className="rules-empty-clear" onClick={handleClear}>
            Clear filters
          </button>
        </div>
      )}

      {/* ── Anonymous Alerts callout — always visible ── */}
      <div className="rules-anon-alerts">
        <div className="rules-anon-header">
          <span className="rules-anon-icon" aria-hidden="true">🔒</span>
          <h2 className="rules-anon-title">Anonymous Alerts</h2>
          <InfoIcon source={SOURCES.anonymousAlerts} />
        </div>
        <p className="rules-anon-body">
          If something feels wrong and you can't talk to a trusted adult, you can report it anonymously.
        </p>
        <p className="rules-anon-body">
          Visit{' '}
          <a
            href="https://www.anonymousalerts.com/leanderisd/"
            target="_blank"
            rel="noopener noreferrer"
            className="rules-anon-link"
          >
            anonymousalerts.com/leanderisd
          </a>
        </p>
        <div className="rules-anon-creds">
          <span>Login: <strong>leanderisd</strong></span>
          <span>Password: <strong>leanderisd</strong></span>
        </div>
        <p className="rules-anon-note">
          Reports are monitored 7 AM – 5 PM on school days.
        </p>
      </div>
    </div>
  );
}
